import React, { useState, useEffect } from 'react';
import { llmConfigService } from '../services/api';

const LlmModelsTab = () => {
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [searchQuery, setSearchQuery] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingModel, setEditingModel] = useState(null);
  const [sliderPresets, setSliderPresets] = useState([]);
  const [isSavingPresets, setIsSavingPresets] = useState(false);
  const [formData, setFormData] = useState({
    modelName: '',
    displayName: '',
    provider: 'vertex',
    defaultTemperature: 1.0,
    costInputTokens: 0,
    costCachedTokens: 0,
    costPriorityInputTokens: 0,
    costPriorityCachedTokens: 0,
    costPriorityOutputTokens: 0,
    costCacheStoragePerHour: 0,
    costOutputTokens: 0,
    maxInputTokens: 1000000,
    maxOutputTokens: 8192,
    isActive: true,
    cacheEnabled: false
  });

  useEffect(() => {
    loadModels();
  }, []);

  const loadModels = async () => {
    try {
      setLoading(true);
      const [modelsRes, presetsRes] = await Promise.all([
        llmConfigService.getAvailableModels(true),
        llmConfigService.getSliderPresets()
      ]);

      const availableModels = modelsRes.data || [];
      const sortedAvailableModels = [...availableModels].sort((a, b) => {
        if (a.isActive !== b.isActive) {
          return a.isActive ? -1 : 1;
        }

        const left = a.displayName || a.modelName || '';
        const right = b.displayName || b.modelName || '';
        return left.localeCompare(right);
      });
      const presets = presetsRes.data || [];
      const firstModelName = sortedAvailableModels[0]?.modelName || '';
      const minimalModel = sortedAvailableModels.find((model) => model.modelName === 'gemini-3-flash-preview')?.modelName;
      const presetByPosition = new Map(presets.map((preset) => [preset.sliderPosition, preset]));
      const mergedPresets = [1, 2, 3].map((position) => {
        const existing = presetByPosition.get(position);
        if (existing) {
          return {
            sliderPosition: position,
            modelName: existing.modelName || firstModelName,
            thinkingLevel: (existing.thinkingLevel || 'low').toLowerCase()
          };
        }

        return {
          sliderPosition: position,
          modelName: position === 1 && minimalModel ? minimalModel : firstModelName,
          thinkingLevel: position === 1 && minimalModel ? 'minimal' : 'low'
        };
      });

      setModels(sortedAvailableModels);
      setSliderPresets(mergedPresets);
    } catch (error) {
      console.error('Error loading models:', error);
      setMessage({ type: 'error', text: 'Failed to load models' });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (model = null) => {
    if (model) {
      setEditingModel(model);
      setFormData({
        modelName: model.modelName,
        displayName: model.displayName,
        provider: model.provider,
        defaultTemperature: parseFloat(model.defaultTemperature),
        costInputTokens: parseFloat(model.costInputTokens),
        costCachedTokens: parseFloat(model.costCachedTokens || 0),
        costPriorityInputTokens: parseFloat(model.costPriorityInputTokens || 0),
        costPriorityCachedTokens: parseFloat(model.costPriorityCachedTokens || 0),
        costPriorityOutputTokens: parseFloat(model.costPriorityOutputTokens || 0),
        costCacheStoragePerHour: parseFloat(model.costCacheStoragePerHour || 0),
        costOutputTokens: parseFloat(model.costOutputTokens),
        maxInputTokens: model.maxInputTokens,
        maxOutputTokens: model.maxOutputTokens,
        isActive: model.isActive,
        cacheEnabled: model.cacheEnabled
      });
    } else {
      setEditingModel(null);
      setFormData({
        modelName: '',
        displayName: '',
        provider: 'vertex',
        defaultTemperature: 1.0,
        costInputTokens: 0,
        costCachedTokens: 0,
        costPriorityInputTokens: 0,
        costPriorityCachedTokens: 0,
        costPriorityOutputTokens: 0,
        costCacheStoragePerHour: 0,
        costOutputTokens: 0,
        maxInputTokens: 1000000,
        maxOutputTokens: 8192,
        isActive: true,
        cacheEnabled: false
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingModel(null);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingModel) {
        await llmConfigService.updateModel(editingModel.id, formData);
        setMessage({ type: 'success', text: 'Model updated successfully' });
      } else {
        await llmConfigService.createModel(formData);
        setMessage({ type: 'success', text: 'Model created successfully' });
      }
      
      await loadModels();
      handleCloseModal();
    } catch (error) {
      console.error('Error saving model:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Failed to save model';
      setMessage({ type: 'error', text: errorMessage });
    }
  };

  const handleDelete = async (modelId, modelName) => {
    if (!window.confirm(`Are you sure you want to delete the model "${modelName}"?`)) {
      return;
    }

    try {
      await llmConfigService.deleteModel(modelId);
      setMessage({ type: 'success', text: 'Model deleted successfully' });
      await loadModels();
    } catch (error) {
      console.error('Error deleting model:', error);
      const errorMessage = error.response?.data?.message || 'Failed to delete model';
      setMessage({ type: 'error', text: errorMessage });
    }
  };

  const handleSetDefault = async (modelId, displayName) => {
    try {
      await llmConfigService.setDefaultModel(modelId);
      setMessage({ type: 'success', text: `"${displayName}" set as default model` });
      await loadModels();
    } catch (error) {
      console.error('Error setting default model:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Failed to set default model';
      setMessage({ type: 'error', text: errorMessage });
    }
  };

  const clearMessage = () => {
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  const handlePresetChange = (sliderPosition, field, value) => {
    setSliderPresets((prev) => prev.map((preset) => {
      if (preset.sliderPosition !== sliderPosition) {
        return preset;
      }

      if (field === 'modelName') {
        const nextThinkingLevel =
          preset.thinkingLevel === 'minimal' && value !== 'gemini-3-flash-preview'
            ? 'low'
            : preset.thinkingLevel;
        return { ...preset, modelName: value, thinkingLevel: nextThinkingLevel };
      }

      return { ...preset, [field]: value };
    }));
  };

  const handleSaveSliderPresets = async () => {
    try {
      if (sliderPresets.some((preset) => !preset.modelName)) {
        setMessage({ type: 'error', text: 'Please choose a model for each slider position.' });
        return;
      }

      const invalidPreset = sliderPresets.find(
        (preset) => preset.thinkingLevel === 'minimal' && preset.modelName !== 'gemini-3-flash-preview'
      );
      if (invalidPreset) {
        setMessage({ type: 'error', text: 'Thinking level "minimal" is only valid for gemini-3-flash-preview.' });
        return;
      }

      setIsSavingPresets(true);
      await Promise.all(
        sliderPresets.map((preset) => llmConfigService.upsertSliderPreset({
          sliderPosition: preset.sliderPosition,
          modelName: preset.modelName,
          thinkingLevel: preset.thinkingLevel,
          isActive: true
        }))
      );

      setMessage({ type: 'success', text: 'Slider presets saved successfully' });
      await loadModels();
    } catch (error) {
      console.error('Error saving slider presets:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Failed to save slider presets';
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setIsSavingPresets(false);
    }
  };

  useEffect(() => {
    if (message.text) clearMessage();
  }, [message]);

  // Filter models based on search query
  const filteredModels = models.filter(model => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      model.modelName?.toLowerCase().includes(query) ||
      model.displayName?.toLowerCase().includes(query) ||
      model.provider?.toLowerCase().includes(query)
    );
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold" style={{color:'rgba(255,255,255,0.85)'}}>LLM Models</h3>
          <p className="text-sm" style={{color:'rgba(255,255,255,0.6)'}}>
            Manage available language models and their configurations.
          </p>
        </div>
        <button
          onClick={() => handleOpenModal()}
          className="btn-primary flex items-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Model
        </button>
      </div>

      {/* Message Display */}
      {message.text && (
        <div className="mb-4 p-3 rounded-lg" style={message.type==='success'?{background:'rgba(130,255,207,0.12)',border:'1px solid rgba(130,255,207,0.3)',color:'rgba(130,255,207,0.9)'}:{background:'rgba(230,100,97,0.12)',border:'1px solid rgba(230,100,97,0.3)',color:'rgba(230,100,97,0.9)'}}>
          {message.text}
        </div>
      )}

      {/* Search Filter */}
      <div className="mb-4">
        <div className="relative">
          <input
            type="text"
            placeholder="Search by model name, display name, or provider..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 rounded-lg focus:outline-none" style={{background:'var(--tqa-surface-2)',border:'1px solid var(--tqa-border-dark)',color:'rgba(255,255,255,0.85)'}}
          />
          <svg 
            className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5" style={{color:'rgba(255,255,255,0.35)'}}
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              style={{color:'rgba(255,255,255,0.4)'}}
              onMouseEnter={e=>e.currentTarget.style.color='rgba(255,255,255,0.75)'}
              onMouseLeave={e=>e.currentTarget.style.color='rgba(255,255,255,0.4)'}
              className="absolute right-3 top-1/2 transform -translate-y-1/2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{borderColor:'var(--tqa-green)'}}></div>
        </div>
      ) : (
        <div className="max-h-[calc(100vh-450px)] overflow-y-auto pr-2 space-y-6">
          {filteredModels.length > 0 ? (
            <div className="grid grid-cols-1 gap-4">
              {filteredModels.map((model) => (
                <ModelCard
                  key={model.id}
                  model={model}
                  onEdit={() => handleOpenModal(model)}
                  onDelete={() => handleDelete(model.id, model.displayName)}
                  onSetDefault={() => handleSetDefault(model.id, model.displayName)}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-12" style={{color:'rgba(255,255,255,0.4)'}}>
              <svg className="w-12 h-12 mx-auto mb-3" style={{color:'rgba(255,255,255,0.3)'}} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
              </svg>
              <p>{searchQuery ? 'No models match your search' : 'No models available'}</p>
            </div>
          )}

          <div className="p-4 rounded-lg" style={{background:'var(--tqa-surface-2)',border:'1px solid var(--tqa-border-dark)'}}>
            <div className="flex items-center justify-between mb-3">
              <div>
                <h4 className="text-sm font-semibold" style={{color:'rgba(255,255,255,0.85)'}}>Model Selection Slider</h4>
                <p className="text-xs" style={{color:'rgba(255,255,255,0.55)'}}>Define the model + thinking level used by slider levels 1 to 3.</p>
              </div>
              <button
                onClick={handleSaveSliderPresets}
                disabled={isSavingPresets || sliderPresets.length === 0}
                className="btn-primary text-xs px-3 py-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSavingPresets ? 'Saving...' : 'Save Presets'}
              </button>
            </div>

            <div className="space-y-2">
              {sliderPresets.map((preset) => (
                <div key={preset.sliderPosition} className="grid grid-cols-1 md:grid-cols-3 gap-2 items-center rounded-md p-2" style={{background:'var(--tqa-surface-3)',border:'1px solid var(--tqa-border-dark)'}}>
                  <div className="text-sm font-medium" style={{color:'rgba(255,255,255,0.7)'}}>Slider {preset.sliderPosition}</div>
                  <select
                    value={preset.modelName || ''}
                    onChange={(e) => handlePresetChange(preset.sliderPosition, 'modelName', e.target.value)}
                    className="w-full px-2 py-2 rounded-md text-sm focus:outline-none" style={{background:'var(--tqa-surface-2)',border:'1px solid var(--tqa-border-dark)',color:'rgba(255,255,255,0.85)'}}
                  >
                    {models.map((model) => (
                      <option key={model.modelName} value={model.modelName}>
                        {model.displayName || model.modelName}
                      </option>
                    ))}
                  </select>
                  <select
                    value={preset.thinkingLevel}
                    onChange={(e) => handlePresetChange(preset.sliderPosition, 'thinkingLevel', e.target.value)}
                    className="w-full px-2 py-2 rounded-md text-sm focus:outline-none" style={{background:'var(--tqa-surface-2)',border:'1px solid var(--tqa-border-dark)',color:'rgba(255,255,255,0.85)'}}
                  >
                    <option value="minimal" disabled={preset.modelName !== 'gemini-3-flash-preview'}>minimal</option>
                    <option value="low">low</option>
                    <option value="medium">medium</option>
                    <option value="high">high</option>
                  </select>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <ModelFormModal
          model={editingModel}
          formData={formData}
          onInputChange={handleInputChange}
          onSubmit={handleSubmit}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
};

const ModelCard = ({ model, onEdit, onDelete, onSetDefault }) => {
  return (
    <div className="rounded-lg p-3 transition-shadow" style={{background:'var(--tqa-surface)',border:model.isDefault?'1px solid rgba(251,191,36,0.5)':'1px solid var(--tqa-border-dark)',boxShadow:model.isDefault?'0 0 0 1px rgba(251,191,36,0.2)':'none'}}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-1.5 mb-1.5">
            <h4 className="text-base font-semibold" style={{color:'rgba(255,255,255,0.85)'}}>{model.displayName}</h4>
            {model.isDefault && (
              <span className="px-2 py-0.5 text-xs font-medium rounded-full" style={{background:'rgba(251,191,36,0.18)',color:'rgba(251,191,36,0.95)',border:'1px solid rgba(251,191,36,0.35)'}}>
                ★ Default
              </span>
            )}
            {model.isActive ? (
              <span className="px-2 py-0.5 text-xs font-medium rounded-full" style={{background:'rgba(130,255,207,0.15)',color:'var(--tqa-green)'}}>
                Active
              </span>
            ) : (
              <span className="px-2 py-0.5 text-xs font-medium rounded-full" style={{background:'rgba(255,255,255,0.08)',color:'rgba(255,255,255,0.45)'}}>
                Inactive
              </span>
            )}
            {model.cacheEnabled && (
              <span className="px-2 py-0.5 text-xs font-medium rounded-full" style={{background:'rgba(130,255,207,0.1)',color:'rgba(130,255,207,0.7)'}}>
                Cache Enabled
              </span>
            )}
          </div>
          
          <p className="text-xs mb-2 font-mono break-all" style={{color:'rgba(255,255,255,0.5)'}}>{model.modelName}</p>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs" style={{color:'rgba(255,255,255,0.55)'}}>
            <div>
              <span className="font-medium">Provider:</span> {model.provider}
            </div>
            <div>
              <span className="font-medium">Temperature:</span> {parseFloat(model.defaultTemperature)}
            </div>
            <div>
              <span className="font-medium">Input Cost:</span> ${parseFloat(model.costInputTokens).toFixed(4)}/1M
            </div>
            <div>
              <span className="font-medium">Cached Cost:</span> ${parseFloat(model.costCachedTokens || 0).toFixed(4)}/1M
            </div>
            <div>
              <span className="font-medium">Cache Storage Cost:</span> ${parseFloat(model.costCacheStoragePerHour || 0).toFixed(4)}/1M token-hour
            </div>
            <div>
              <span className="font-medium">Output Cost:</span> ${parseFloat(model.costOutputTokens).toFixed(4)}/1M
            </div>
            <div>
              <span className="font-medium">Priority Input Cost:</span> ${parseFloat(model.costPriorityInputTokens || 0).toFixed(4)}/1M
            </div>
            <div>
              <span className="font-medium">Priority Cached Cost:</span> ${parseFloat(model.costPriorityCachedTokens || 0).toFixed(4)}/1M
            </div>
            <div>
              <span className="font-medium">Priority Output Cost:</span> ${parseFloat(model.costPriorityOutputTokens || 0).toFixed(4)}/1M
            </div>
            <div>
              <span className="font-medium">Max Input:</span> {model.maxInputTokens.toLocaleString()}
            </div>
            <div>
              <span className="font-medium">Max Output:</span> {model.maxOutputTokens.toLocaleString()}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-1.5 ml-3">
          {model.isActive && !model.isDefault && (
            <button
              onClick={onSetDefault}
              style={{color:'rgba(251,191,36,0.8)',borderRadius:'6px'}}
              onMouseEnter={e=>e.currentTarget.style.background='rgba(251,191,36,0.12)'}
              onMouseLeave={e=>e.currentTarget.style.background='transparent'}
              className="p-1.5 transition-colors"
              title="Set as default model"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
              </svg>
            </button>
          )}
          <button
            onClick={onEdit}
            style={{color:'rgba(130,255,207,0.75)',borderRadius:'6px'}}
            onMouseEnter={e=>e.currentTarget.style.background='rgba(130,255,207,0.1)'}
            onMouseLeave={e=>e.currentTarget.style.background='transparent'}
            className="p-1.5 transition-colors"
            title="Edit model"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
          <button
            onClick={onDelete}
            style={{color:'rgba(230,100,97,0.8)',borderRadius:'6px'}}
            onMouseEnter={e=>e.currentTarget.style.background='rgba(230,100,97,0.1)'}
            onMouseLeave={e=>e.currentTarget.style.background='transparent'}
            className="p-1.5 transition-colors"
            title="Delete model"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

const ModelFormModal = ({ model, formData, onInputChange, onSubmit, onClose }) => {
  const isEditing = !!model;
  const sectionContainerStyle = { background: 'var(--tqa-surface-2)', border: '1px solid var(--tqa-border-dark)' };
  const sectionTitleStyle = { color: 'rgba(255,255,255,0.78)' };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" style={{background:'var(--tqa-surface)'}}>
        <div className="p-6" style={{borderBottom:'1px solid var(--tqa-border-dark)'}}>
          <h3 className="text-xl font-semibold" style={{color:'rgba(255,255,255,0.85)'}}>
            {isEditing ? 'Edit Model' : 'Add New Model'}
          </h3>
        </div>

        <form onSubmit={onSubmit} className="p-6 space-y-4">
          <div className="rounded-lg p-4 space-y-3" style={sectionContainerStyle}>
            <h4 className="text-sm font-semibold uppercase tracking-wide" style={sectionTitleStyle}>General Details</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1" style={{color:'rgba(255,255,255,0.65)'}}>
                  Model Name *
                </label>
                <input
                  type="text"
                  value={formData.modelName}
                  onChange={(e) => onInputChange('modelName', e.target.value)}
                  disabled={isEditing}
                  required
                  className="w-full px-3 py-2 rounded-md focus:outline-none disabled:opacity-40" style={{background:'var(--tqa-surface-2)',border:'1px solid var(--tqa-border-dark)',color:'rgba(255,255,255,0.85)'}}
                  placeholder="e.g., gemini-2.5-pro"
                />
                {isEditing && (
                  <p className="text-xs mt-1" style={{color:'rgba(255,255,255,0.4)'}}>Model name cannot be changed</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1" style={{color:'rgba(255,255,255,0.65)'}}>
                  Display Name *
                </label>
                <input
                  type="text"
                  value={formData.displayName}
                  onChange={(e) => onInputChange('displayName', e.target.value)}
                  required
                  className="w-full px-3 py-2 rounded-md focus:outline-none" style={{background:'var(--tqa-surface-2)',border:'1px solid var(--tqa-border-dark)',color:'rgba(255,255,255,0.85)'}}
                  placeholder="e.g., Gemini 2.5 Pro"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1" style={{color:'rgba(255,255,255,0.65)'}}>
                  Provider *
                </label>
                <select
                  value={formData.provider}
                  onChange={(e) => onInputChange('provider', e.target.value)}
                  required
                  className="w-full px-3 py-2 rounded-md focus:outline-none" style={{background:'var(--tqa-surface-2)',border:'1px solid var(--tqa-border-dark)',color:'rgba(255,255,255,0.85)'}}
                >
                  <option value="vertex">Vertex AI</option>
                  <option value="vertex-maas">Vertex AI MaaS</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1" style={{color:'rgba(255,255,255,0.65)'}}>
                  Default Temperature *
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="2"
                  value={formData.defaultTemperature}
                  onChange={(e) => onInputChange('defaultTemperature', parseFloat(e.target.value))}
                  required
                  className="w-full px-3 py-2 rounded-md focus:outline-none" style={{background:'var(--tqa-surface-2)',border:'1px solid var(--tqa-border-dark)',color:'rgba(255,255,255,0.85)'}}
                />
              </div>
            </div>
          </div>

          <div className="rounded-lg p-4 space-y-3" style={sectionContainerStyle}>
            <h4 className="text-sm font-semibold uppercase tracking-wide" style={sectionTitleStyle}>Standard Tokens Cost</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1" style={{color:'rgba(255,255,255,0.65)'}}>
                  Cost Input Tokens (per 1M) *
                </label>
                <input
                  type="number"
                  step="0.0001"
                  min="0"
                  value={formData.costInputTokens}
                  onChange={(e) => onInputChange('costInputTokens', parseFloat(e.target.value))}
                  required
                  className="w-full px-3 py-2 rounded-md focus:outline-none" style={{background:'var(--tqa-surface-2)',border:'1px solid var(--tqa-border-dark)',color:'rgba(255,255,255,0.85)'}}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1" style={{color:'rgba(255,255,255,0.65)'}}>
                  Cost Output Tokens (per 1M) *
                </label>
                <input
                  type="number"
                  step="0.0001"
                  min="0"
                  value={formData.costOutputTokens}
                  onChange={(e) => onInputChange('costOutputTokens', parseFloat(e.target.value))}
                  required
                  className="w-full px-3 py-2 rounded-md focus:outline-none" style={{background:'var(--tqa-surface-2)',border:'1px solid var(--tqa-border-dark)',color:'rgba(255,255,255,0.85)'}}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1" style={{color:'rgba(255,255,255,0.65)'}}>
                  Cost Cached Tokens (per 1M)
                </label>
                <input
                  type="number"
                  step="0.0001"
                  min="0"
                  value={formData.costCachedTokens}
                  onChange={(e) => onInputChange('costCachedTokens', parseFloat(e.target.value || 0))}
                  className="w-full px-3 py-2 rounded-md focus:outline-none" style={{background:'var(--tqa-surface-2)',border:'1px solid var(--tqa-border-dark)',color:'rgba(255,255,255,0.85)'}}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1" style={{color:'rgba(255,255,255,0.65)'}}>
                  Cache Storage Cost (per 1M token-hour)
                </label>
                <input
                  type="number"
                  step="0.0001"
                  min="0"
                  value={formData.costCacheStoragePerHour}
                  onChange={(e) => onInputChange('costCacheStoragePerHour', parseFloat(e.target.value || 0))}
                  className="w-full px-3 py-2 rounded-md focus:outline-none" style={{background:'var(--tqa-surface-2)',border:'1px solid var(--tqa-border-dark)',color:'rgba(255,255,255,0.85)'}}
                />
              </div>
            </div>
          </div>

          <div className="rounded-lg p-4 space-y-3" style={sectionContainerStyle}>
            <h4 className="text-sm font-semibold uppercase tracking-wide" style={sectionTitleStyle}>Priority Token Cost</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1" style={{color:'rgba(255,255,255,0.65)'}}>
                  Priority Input Cost (per 1M)
                </label>
                <input
                  type="number"
                  step="0.0001"
                  min="0"
                  value={formData.costPriorityInputTokens}
                  onChange={(e) => onInputChange('costPriorityInputTokens', parseFloat(e.target.value || 0))}
                  className="w-full px-3 py-2 rounded-md focus:outline-none" style={{background:'var(--tqa-surface-2)',border:'1px solid var(--tqa-border-dark)',color:'rgba(255,255,255,0.85)'}}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1" style={{color:'rgba(255,255,255,0.65)'}}>
                  Priority Output Cost (per 1M)
                </label>
                <input
                  type="number"
                  step="0.0001"
                  min="0"
                  value={formData.costPriorityOutputTokens}
                  onChange={(e) => onInputChange('costPriorityOutputTokens', parseFloat(e.target.value || 0))}
                  className="w-full px-3 py-2 rounded-md focus:outline-none" style={{background:'var(--tqa-surface-2)',border:'1px solid var(--tqa-border-dark)',color:'rgba(255,255,255,0.85)'}}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1" style={{color:'rgba(255,255,255,0.65)'}}>
                  Priority Cached Cost (per 1M)
                </label>
                <input
                  type="number"
                  step="0.0001"
                  min="0"
                  value={formData.costPriorityCachedTokens}
                  onChange={(e) => onInputChange('costPriorityCachedTokens', parseFloat(e.target.value || 0))}
                  className="w-full px-3 py-2 rounded-md focus:outline-none" style={{background:'var(--tqa-surface-2)',border:'1px solid var(--tqa-border-dark)',color:'rgba(255,255,255,0.85)'}}
                />
              </div>
            </div>
          </div>

          <div className="rounded-lg p-4 space-y-3" style={sectionContainerStyle}>
            <h4 className="text-sm font-semibold uppercase tracking-wide" style={sectionTitleStyle}>Max Tokens</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1" style={{color:'rgba(255,255,255,0.65)'}}>
                  Max Input Tokens *
                </label>
                <input
                  type="number"
                  min="1"
                  value={formData.maxInputTokens}
                  onChange={(e) => onInputChange('maxInputTokens', parseInt(e.target.value))}
                  required
                  className="w-full px-3 py-2 rounded-md focus:outline-none" style={{background:'var(--tqa-surface-2)',border:'1px solid var(--tqa-border-dark)',color:'rgba(255,255,255,0.85)'}}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1" style={{color:'rgba(255,255,255,0.65)'}}>
                  Max Output Tokens *
                </label>
                <input
                  type="number"
                  min="1"
                  value={formData.maxOutputTokens}
                  onChange={(e) => onInputChange('maxOutputTokens', parseInt(e.target.value))}
                  required
                  className="w-full px-3 py-2 rounded-md focus:outline-none" style={{background:'var(--tqa-surface-2)',border:'1px solid var(--tqa-border-dark)',color:'rgba(255,255,255,0.85)'}}
                />
              </div>
            </div>
          </div>

          <div className="rounded-lg p-4 space-y-3" style={sectionContainerStyle}>
            <h4 className="text-sm font-semibold uppercase tracking-wide" style={sectionTitleStyle}>Flags</h4>
            <div className="flex gap-6">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => onInputChange('isActive', e.target.checked)}
                  className="w-4 h-4 rounded accent-tqa-green"
                />
                <span className="text-sm" style={{color:'rgba(255,255,255,0.7)'}}>Active</span>
              </label>

              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.cacheEnabled}
                  onChange={(e) => onInputChange('cacheEnabled', e.target.checked)}
                  className="w-4 h-4 rounded accent-tqa-green"
                />
                <span className="text-sm" style={{color:'rgba(255,255,255,0.7)'}}>Cache Enabled</span>
              </label>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4" style={{borderTop:'1px solid var(--tqa-border-dark)'}}>
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
            >
              {isEditing ? 'Update Model' : 'Create Model'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LlmModelsTab;
